import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';

import { generateCacheKey } from '@msdyn365-commerce-modules/retail-actions';
import { bsGetCategoryDataAsync } from '../custom-proxy/DataActionExtensions.g';
import { ICategoryExt } from '../custom-proxy/DataServiceEntities.g';

/**
 * Input class for CategoryExtraData data action.
 */
export class CategoryExtraDataInput implements IActionInput {
    public categoryId?: number;

    public includeSeoData?: boolean;

    private readonly apiSettings: ICommerceApiSettings;

    constructor(context: IRequestContext, includeSeoData?: boolean) {
        this.includeSeoData = includeSeoData;
        let categoryId;
        if (context.query && context.query.categoryId) {
            categoryId = Number(context.query.categoryId);

            if (Number.isNaN(categoryId)) {
                throw new Error('Failed to cast category id into a number.');
            }
        } else if (context.urlTokens && context.urlTokens.recordId) {
            categoryId = Number(context.urlTokens.recordId);
            if (Number.isNaN(categoryId)) {
                throw new Error('Failed to cast category id into a number.');
            }
        }

        this.categoryId = categoryId;
        this.apiSettings = context.apiSettings;
    }

    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'CategoryId', value: this.categoryId },
            { key: 'IncludeSeoData', value: this.includeSeoData }
        );

    public getCacheObjectType = () => 'Category-Extra-Data';

    public dataCacheType = (): CacheType => 'application';
}

/**
 * Creates a current category input from an ICreateActionContext.
 * @param inputData
 */
export const createGetCategoryExtraDataInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    if (inputData && inputData.requestContext) {
        return new CategoryExtraDataInput(inputData.requestContext, true);
    }

    throw new Error('Please specify categoryId query string in request.');
};

/**
 * Action method for the getCategoryExtraData Data Action.
 * @param input The action input class.
 * @param ctx The action context.
 */
export async function getCategoryExtraDataAction(input: CategoryExtraDataInput, ctx: IActionContext): Promise<ICategoryExt | undefined> {
    if (input.categoryId) {
        const category = await bsGetCategoryDataAsync(
            { callerContext: ctx, queryResultSettings: {} },
            input.categoryId,
            input.includeSeoData ?? false
        );
        if (!category) {
            ctx.trace('[getCategoryExtraData] Unable to get categories from server');
            return;
        }

        return category;
    }
    return;
}

/**
 * The getCategoryExtraData data action
 * Makes a call to the BsGetCategoryData RetailServer API
 * based on the categoryId query param set in the URL of the request.
 */
export const getCategoryExtraDataActionDataAction = createObservableDataAction({
    id: 'get-category-extra-data',
    action: <IAction<ICategoryExt>>getCategoryExtraDataAction,
    input: createGetCategoryExtraDataInput
});

export default getCategoryExtraDataActionDataAction;
